import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight, Sparkles, ShieldCheck, BookOpen, MessageCircle, Star,
  Quote, Flame, MapPin, Clock, Languages, CheckCircle2,
} from "lucide-react";
import Seo from "../components/Seo";
import Icon from "../components/Icon";
import LanguageSwitcher from "../components/LanguageSwitcher";
import { Reveal, Parallax } from "../components/Reveal";
import FestivalStrip from "../components/FestivalStrip";
import CountdownBanner from "../components/CountdownBanner";
import DailyDarshan from "../components/DailyDarshan";
import Testimonials from "../components/Testimonials";
import { useLang } from "../i18n/LanguageContext";
import { SITE } from "../i18n/translations";
import { useBooking } from "../context/BookingContext";
import { SERVICES } from "../data/services";
import { PILLARS } from "../data/pillars";
import { CONCERNS } from "../data/lifeConcerns";

const IMG = {
  hero: "https://images.unsplash.com/photo-1760967585313-fe7304f211fa?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200",
  lamp: "https://images.unsplash.com/photo-1784815130752-60dec0d7a736?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  marigold: "https://images.unsplash.com/photo-1699764681875-dd04ce36b1c3?crop=entropy&cs=srgb&fm=jpg&q=85&w=800",
  altar: "https://images.pexels.com/photos/38389819/pexels-photo-38389819.jpeg?auto=compress&cs=tinysrgb&w=1000",
};

const fadeUp = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.65, delay: i * 0.09, ease: [0.22, 1, 0.36, 1] } }),
};

const MARQUEE = [
  "Tarapith Tantra", "Maa Tara Sadhana", "Dashamahavidya", "Mahasmasan Tradition",
  "Kaushiki Amavasya", "Vedic Jyotish", "Puja & Havan", "Shakta Heritage",
];

const PROCESS = [
  { icon: "MousePointerClick", title: "Choose your concern", desc: "Select the service or life concern closest to what you are facing right now." },
  { icon: "PenLine", title: "Share a few details", desc: "A short guided form — name, place and your concern. Birth details only if relevant." },
  { icon: "MessageCircle", title: "Continue on WhatsApp", desc: "Your enquiry opens a pre-filled WhatsApp message and Shivam Chatterjee replies personally." },
];

const TRUST = [
  { icon: ShieldCheck, label: "Authentic tradition", value: "Tarapith lineage" },
  { icon: Languages, label: "Guidance in", languages: true },
  { icon: MapPin, label: "Sacred seat", value: "Birbhum, Bengal" },
  { icon: Clock, label: "Personal reply", value: "Usually within a day" },
];

const HERO_LANGS = [
  { code: "en", label: "EN" },
  { code: "bn", label: "বাংলা" },
  { code: "hi", label: "हिन्दी" },
];


export default function Home() {
  const { t, pick, lang, setLang } = useLang();
  const { openBooking } = useBooking();

  const homeJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: [
      { "@type": "Question", name: "What is Tarapith Tantra?", acceptedAnswer: { "@type": "Answer", text: "Tarapith Tantra is the living Shakta-Tantric tradition centred on the worship of Maa Tara at Tarapith in Birbhum, West Bengal, closely linked with the Mahasmasan cremation ground and disciplined spiritual sadhana." } },
      { "@type": "Question", name: "Who is Shivam Chatterjee?", acceptedAnswer: { "@type": "Answer", text: "Shivam Chatterjee is a Tantrik sadhak and Vedic astrologer offering respectful spiritual guidance, puja, havan and astrology consultations rooted in the Tarapith tradition." } },
      { "@type": "Question", name: "How can I book a puja or consultation?", acceptedAnswer: { "@type": "Answer", text: "Select a concern or service on TarapithTantrik.com to begin a guided enquiry, then continue the conversation directly on WhatsApp at +91 83728 34728." } },
    ],
  };

  return (
    <>
      <Seo
        title="Tarapith Tantra, Maa Tara & Spiritual Guidance"
        description="The authoritative destination for Tarapith Tantra, Maa Tara sadhana, Dashamahavidya and Shakta traditions. Respectful spiritual guidance, puja, havan and Vedic astrology with Shivam Chatterjee."
        path="/"
        keywords={["Tarapith Tantra", "Maa Tara", "Tarapith temple", "Shakta Tantra", "Dashamahavidya", "Shivam Chatterjee", "Tarapith puja booking"]}
        jsonLd={homeJsonLd}
      />

      {/* ── HERO ───────────────────────────────────────── */}
      <section className="relative overflow-hidden" data-testid="hero-section">
        <div className="pattern-sacred absolute inset-0 -z-10 opacity-40" />
        <span className="orb orb-gold -z-10 h-72 w-72 -top-16 left-1/3 float-slow" />
        <span className="orb orb-red -z-10 h-64 w-64 top-1/2 -right-10 float-slow" />
        <div className="absolute -top-32 -left-24 h-[420px] w-[420px] rounded-full bg-gradient-to-br from-tara-goldAccent/25 to-transparent blur-3xl -z-10" />
        <div className="absolute top-24 right-0 h-[380px] w-[380px] rounded-full bg-gradient-to-bl from-tara-vermilion/15 to-transparent blur-3xl -z-10" />

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-14 pb-16 sm:pt-20 lg:pt-24">
          <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-12 lg:gap-16 items-center">
            <motion.div initial="hidden" animate="show" variants={fadeUp}>
              <motion.span
                variants={fadeUp} custom={0}
                className="badge-gold inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-[11px] font-serif tracking-[0.28em] uppercase mb-7"
              >
                <Sparkles className="h-3.5 w-3.5" /> {t("hero_eyebrow")}
              </motion.span>

              <motion.h1 variants={fadeUp} custom={1} className="font-serif text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.04] mb-5">
                <span className="text-gradient-flame">{t("hero_title")}</span>
              </motion.h1>

              <motion.p variants={fadeUp} custom={2} className="font-display text-2xl sm:text-3xl text-tara-sindoor italic mb-5">
                Sacred guidance with {SITE.practitioner}
              </motion.p>

              <motion.p variants={fadeUp} custom={3} className="text-base md:text-lg text-tara-body leading-relaxed max-w-xl mb-9">
                {t("hero_subtitle")}
              </motion.p>

              <motion.div variants={fadeUp} custom={4} className="flex flex-wrap items-center gap-4">
                <button
                  onClick={() => openBooking("maa-tara-puja")}
                  className="btn-sacred aura-pulse flex items-center gap-2.5 rounded-full px-8 py-4 font-serif font-semibold tracking-wide"
                  data-testid="hero-book-btn"
                >
                  <MessageCircle className="h-5 w-5" /> {t("cta_book")}
                </button>
                <Link
                  to="/knowledge"
                  className="btn-ghost-gold flex items-center gap-2.5 rounded-full px-8 py-4 font-serif font-semibold"
                  data-testid="hero-explore-btn"
                >
                  <BookOpen className="h-5 w-5" /> {t("cta_explore")}
                </Link>
                <div className="sm:hidden"><LanguageSwitcher /></div>
              </motion.div>

              <motion.div variants={fadeUp} custom={5} className="mt-11 grid grid-cols-2 sm:grid-cols-4 gap-x-6 gap-y-6">
                {TRUST.map((x) => (
                  <div key={x.label} className="flex items-start gap-2.5" data-testid={`hero-trust-${x.label.replace(/\s/g, "-").toLowerCase()}`}>
                    <x.icon className="h-4 w-4 text-tara-gold mt-0.5 shrink-0" />
                    <div>
                      <p className="text-[10px] uppercase tracking-[0.16em] text-tara-muted">{x.label}</p>
                      {x.languages ? (
                        <span className="mt-0.5 flex items-center gap-1" data-testid="hero-language-tabs">
                          {HERO_LANGS.map((l, li) => (
                            <React.Fragment key={l.code}>
                              {li > 0 && <span className="text-tara-muted/50 text-xs">/</span>}
                              <button
                                onClick={() => setLang(l.code)}
                                className={`rounded-md px-1.5 py-0.5 text-sm font-semibold transition-all ${
                                  lang === l.code
                                    ? "bg-gradient-to-r from-tara-teal to-tara-vermilion text-white"
                                    : "text-tara-ink hover:text-tara-vermilion"
                                }`}
                                data-testid={`hero-lang-${l.code}`}
                                aria-pressed={lang === l.code}
                              >
                                {l.label}
                              </button>
                            </React.Fragment>
                          ))}
                        </span>
                      ) : (
                        <p className="text-sm font-semibold text-tara-ink">{x.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </motion.div>
            </motion.div>

            {/* Layered sacred collage */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
              className="relative hidden sm:block"
              data-testid="hero-collage"
            >
              <div className="absolute inset-6 rounded-full border border-tara-gold/25 spin-slow" />
              <div className="absolute inset-16 rounded-full border border-dashed border-tara-vermilion/20 spin-slow" />

              <Parallax distance={18}>
              <div className="relative rounded-[2rem] overflow-hidden shadow-lifted border border-tara-gold/25">
                <img src={IMG.hero} alt="Sacred idol of the Divine Mother adorned for worship at Tarapith" className="h-[420px] lg:h-[500px] w-full object-cover" loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-[#14262A]/55 via-transparent to-white/10" />
                <div className="absolute bottom-4 left-4 right-4 glass-liquid rounded-2xl px-5 py-4 flex items-center gap-4">
                  <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-tara-gold to-tara-vermilion flame-flicker">
                    <Flame className="h-5 w-5 text-white" />
                  </span>
                  <div className="min-w-0">
                    <p className="font-serif text-sm font-bold text-tara-ink truncate">{SITE.practitioner}</p>
                    <p className="text-[11px] text-tara-muted">Tantrik Sadhak &amp; Vedic Astrologer</p>
                  </div>
                  <span className="ml-auto flex items-center gap-1 text-tara-gold">
                    {[0, 1, 2, 3, 4].map((i) => <Star key={i} className="h-3.5 w-3.5 fill-current" />)}
                  </span>
                </div>
              </div>
              </Parallax>

              <motion.div
                className="absolute top-1/4 -left-10 h-32 w-32 rounded-3xl overflow-hidden border-4 border-white shadow-lifted float-slow hidden lg:block"
              >
                <img src={IMG.marigold} alt="Marigold flowers for offering" className="h-full w-full object-cover" loading="lazy" />
              </motion.div>
              <div className="absolute -top-6 -right-4 glass-liquid rounded-2xl px-4 py-3 hidden lg:block float-slow">
                <p className="text-[10px] uppercase tracking-[0.18em] text-tara-muted">WhatsApp</p>
                <p className="text-sm font-semibold text-tara-ink">{SITE.phoneDisplay}</p>
              </div>
            </motion.div>
          </div>
        </div>

        {/* Sacred marquee */}
        <div className="border-y border-tara-gold/20 bg-white/50 backdrop-blur-md py-3.5 marquee-mask" data-testid="hero-marquee">
          <div className="marquee-track gap-10">
            {[...MARQUEE, ...MARQUEE].map((m, i) => (
              <span key={i} className="flex items-center gap-10 text-xs font-serif tracking-[0.28em] uppercase text-tara-goldDeep/80 whitespace-nowrap">
                {m} <span className="text-tara-vermilion/50">◆</span>
              </span>
            ))}
          </div>
        </div>
      </section>

      <DailyDarshan />

      <CountdownBanner />

      {/* ── SERVICES (uniform boxes) ───────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20 sm:py-24" data-testid="services-section" id="services">
        <Reveal variant="blur" className="max-w-2xl mb-12">
          <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">{t("services_eyebrow")}</span>
          <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3 mb-4 leading-tight">{t("services_title")}</h2>
          <div className="rule-gold mb-5" />
          <p className="text-base text-tara-body">{t("services_sub")}</p>
        </Reveal>

        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} variant="up" delay={(i % 4) * 0.06} className="h-full">
              <button
                onClick={() => openBooking(s.id)}
                className="glass bento-tile frame-gold group flex h-full w-full flex-col rounded-[1.5rem] p-6 text-left"
                data-testid={`service-card-${s.id}`}
              >
                <span
                  className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl shadow-soft"
                  style={{ background: `linear-gradient(140deg, ${s.color}1A, #ffffff)`, border: `1px solid ${s.color}40` }}
                >
                  <Icon name={s.icon} className="h-5 w-5" style={{ color: s.color }} />
                </span>
                <h3 className="font-display text-xl font-semibold text-tara-ink leading-snug mb-2">{pick(s, "title")}</h3>
                <p className="text-sm text-tara-body leading-relaxed line-clamp-3">{pick(s, "desc")}</p>
                <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-tara-teal group-hover:gap-3 transition-all">
                  {t("start_enquiry")} <ArrowRight className="h-3.5 w-3.5" />
                </span>
              </button>
            </Reveal>
          ))}
        </div>

        {/* ── LIFE CONCERNS (same box language, right below the services) ── */}
        <div className="mt-20" data-testid="concerns-section">
          <Reveal variant="blur" className="max-w-2xl mb-10">
            <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">{t("concerns_eyebrow")}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3">{t("concerns_title")}</h2>
            <div className="rule-gold mt-5" />
          </Reveal>

          <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {CONCERNS.map((c, i) => (
              <Reveal key={c.id} variant="up" delay={(i % 4) * 0.06} className="h-full">
                <button
                  onClick={() => openBooking(c.serviceId, pick(c, "title"))}
                  className="glass bento-tile frame-gold group flex h-full w-full flex-col rounded-[1.5rem] p-6 text-left"
                  data-testid={`concern-chip-${c.id}`}
                >
                  <span className="mb-5 flex h-12 w-12 items-center justify-center rounded-2xl border border-tara-vermilion/25 bg-tara-vermilion/[0.06]">
                    <Icon name={c.icon} className="h-5 w-5 text-tara-vermilion" />
                  </span>
                  <h3 className="font-display text-xl font-semibold text-tara-ink leading-snug mb-2">{pick(c, "title")}</h3>
                  {pick(c, "desc") && (
                    <p className="text-sm text-tara-body leading-relaxed line-clamp-3">{pick(c, "desc")}</p>
                  )}
                  <span className="mt-auto pt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-tara-teal group-hover:gap-3 transition-all">
                    {t("start_enquiry")} <ArrowRight className="h-3.5 w-3.5" />
                  </span>
                </button>
              </Reveal>
            ))}
          </div>

          <Link
            to="/life-concerns"
            className="mt-9 inline-flex items-center gap-1.5 text-tara-goldDeep font-serif font-semibold hover:gap-3 transition-all"
            data-testid="concerns-view-all"
          >
            {t("nav_concerns")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </section>

      {/* ── PROCESS ────────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-testid="process-section">
        <div className="glass-liquid rounded-[2rem] p-8 sm:p-12 relative overflow-hidden">
          <div className="pattern-sacred absolute inset-0 opacity-30" />
          <div className="relative">
            <div className="max-w-xl mb-10">
              <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">Simple &amp; respectful</span>
              <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3">Three steps to guidance</h2>
              <div className="rule-gold mt-5" />
            </div>
            <div className="grid gap-8 sm:grid-cols-3">
              {PROCESS.map((p, i) => (
                <motion.div
                  key={p.title}
                  variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
                  className="relative"
                  data-testid={`process-step-${i + 1}`}
                >
                  <span className="font-serif text-5xl font-bold text-tara-gold/25 leading-none">0{i + 1}</span>
                  <div className="mt-3 flex items-center gap-2.5 mb-2">
                    <Icon name={p.icon} className="h-5 w-5 text-tara-vermilion" />
                    <h3 className="font-display text-xl font-semibold text-tara-ink">{p.title}</h3>
                  </div>
                  <p className="text-sm text-tara-body leading-relaxed">{p.desc}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <FestivalStrip />

      {/* ── KNOWLEDGE PILLARS ──────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-testid="pillars-section">
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-5 mb-12">
          <div className="max-w-xl">
            <span className="text-[11px] font-serif tracking-[0.28em] uppercase text-tara-goldDeep">{t("pillars_eyebrow")}</span>
            <h2 className="font-serif text-3xl sm:text-4xl font-semibold text-tara-ink mt-3">{t("pillars_title")}</h2>
            <div className="rule-gold my-5" />
            <p className="text-base text-tara-body">{t("pillars_sub")}</p>
          </div>
          <Link to="/knowledge" className="inline-flex items-center gap-1.5 text-tara-goldDeep font-serif font-semibold hover:gap-3 transition-all" data-testid="pillars-view-all">
            {t("nav_knowledge")} <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
        <div className="grid gap-7 sm:grid-cols-2 lg:grid-cols-3">
          {PILLARS.slice(0, 6).map((p, i) => (
            <motion.div key={p.slug} variants={fadeUp} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i % 3}>
              <Link to={`/knowledge/${p.slug}`} className="glass hover-lift group block rounded-[1.6rem] overflow-hidden h-full" data-testid={`pillar-card-${p.slug}`}>
                <div className="h-44 overflow-hidden relative bg-tara-sand">
                  <img src={p.image} alt={pick(p, "title")} className="h-full w-full object-cover group-hover:scale-[1.06] transition-transform duration-700" loading="lazy" />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#14262A]/60 via-[#14262A]/10 to-transparent" />
                  <span className="absolute bottom-3.5 left-4 flex h-10 w-10 items-center justify-center rounded-xl bg-white/90 backdrop-blur border border-tara-gold/40 shadow-soft">
                    <Icon name={p.icon} className="h-5 w-5 text-tara-vermilion" />
                  </span>
                </div>
                <div className="p-6">
                  <h3 className="font-display text-2xl font-semibold text-tara-ink mb-2">{pick(p, "title")}</h3>
                  <p className="text-sm text-tara-body leading-relaxed line-clamp-3 mb-4">{pick(p, "summary")}</p>
                  <span className="inline-flex items-center gap-1.5 text-sm font-serif font-semibold text-tara-goldDeep group-hover:gap-3 transition-all">
                    {t("read_more")} <ArrowRight className="h-4 w-4" />
                  </span>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ── AUTHORITY ──────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-20 sm:pb-24" data-testid="authority-section">
        <div className="grid lg:grid-cols-[1.15fr_1fr] gap-10 lg:gap-14 items-center">
          <div>
            <Quote className="h-9 w-9 text-tara-gold/50 mb-5" />
            <h2 className="font-display text-3xl sm:text-4xl lg:text-[2.75rem] font-semibold text-tara-ink mb-5 leading-[1.15]">
              Guidance rooted in the living tradition of Tarapith
            </h2>
            <p className="text-base text-tara-body leading-relaxed mb-6 max-w-xl">
              {SITE.practitioner} offers respectful spiritual guidance, puja, havan and Vedic astrology consultation grounded
              in the authentic Shakta-Tantric heritage of Tarapith and Maa Tara — with honesty about what tradition can
              and cannot promise.
            </p>
            <ul className="space-y-3 mb-8">
              {[
                "Verified history, tradition and folklore clearly distinguished",
                "No guaranteed supernatural outcomes, ever",
                "Never a substitute for medical, legal or financial advice",
              ].map((x) => (
                <li key={x} className="flex items-start gap-3 text-sm text-tara-body">
                  <CheckCircle2 className="h-5 w-5 text-tara-gold shrink-0 mt-0.5" /> {x}
                </li>
              ))}
            </ul>
            <div className="flex flex-wrap gap-4">
              <Link to="/about" className="btn-ghost-gold inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-serif font-semibold" data-testid="authority-about-btn">
                {t("nav_about")} <ArrowRight className="h-4 w-4" />
              </Link>
              <button onClick={() => openBooking()} className="btn-sacred inline-flex items-center gap-2 rounded-full px-7 py-3.5 font-serif font-semibold" data-testid="authority-speak-btn">
                <MessageCircle className="h-4 w-4" /> {t("cta_speak")}
              </button>
            </div>
          </div>
          <div className="relative">
            <div className="absolute -inset-3 rounded-[2rem] bg-gradient-to-br from-tara-goldAccent/25 to-tara-vermilion/10 blur-2xl" />
            <Parallax distance={34}>
            <img
              src={IMG.altar}
              alt="Illuminated sacred altar at Tarapith"
              className="relative rounded-[1.8rem] object-cover w-full h-80 lg:h-[26rem] border border-tara-gold/25 shadow-lifted"
              loading="lazy"
            />
            </Parallax>
          </div>
        </div>
      </section>

      <Testimonials />

      {/* ── FINAL CTA ──────────────────────────────────── */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24" data-testid="final-cta-section">
        <div className="relative overflow-hidden rounded-[2rem] bg-gradient-to-br from-[#8E1B22] via-[#B4232A] to-[#0E5A5E] p-10 sm:p-16 text-center shadow-lifted">
          <div className="pattern-sacred absolute inset-0 opacity-20" />
          <div className="relative">
            <Flame className="h-10 w-10 text-white/90 mx-auto mb-6 flame-flicker" />
            <h2 className="font-serif text-3xl sm:text-4xl font-bold text-white mb-4 leading-tight">
              Begin your enquiry with Maa Tara's blessings
            </h2>
            <p className="text-white/85 max-w-xl mx-auto mb-9">
              Share your concern in a short guided form — your message continues directly on WhatsApp with {SITE.practitioner}.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <button
                onClick={() => openBooking("maa-tara-puja")}
                className="flex items-center gap-2.5 rounded-full bg-white px-8 py-4 font-serif font-semibold text-tara-sindoor hover:-translate-y-0.5 hover:shadow-xl transition-all"
                data-testid="final-cta-book-btn"
              >
                <MessageCircle className="h-5 w-5" /> {t("cta_request")}
              </button>
              <a
                href={`https://wa.me/${SITE.whatsapp}`}
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-2.5 rounded-full border border-white/50 px-8 py-4 font-serif font-semibold text-white hover:bg-white/10 transition-all"
                data-testid="final-cta-whatsapp-btn"
              >
                {SITE.phoneDisplay}
              </a>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
